import type { FC, ReactNode } from 'react'

import { cn } from '@/lib/utils'

interface SplitCharsProps {
    text: string
    className?: string
    charClassName?: string
}

// Splits static copy into per-character spans ([data-char]) for staggered reveals.
// Letters of one word are kept together in a no-wrap group, so a title such as GOODS can never
// break between its letters; lines only wrap at spaces.
// Screen readers get the whole phrase once; the split letters are hidden from them.
const SplitChars: FC<SplitCharsProps> = ({ text, className, charClassName }): ReactNode => {
    const parts = text.split(/(\s+)/).filter(part => part.length > 0)

    return (
        <span className={className}>
            <span className='sr-only'>{text}</span>
            <span aria-hidden='true'>
                {parts.map((part, partIndex) => (
                    /^\s+$/.test(part)
                        ? <span key={`space-${partIndex}`} className='whitespace-pre'>{part}</span>
                        : (
                            <span key={`word-${partIndex}`} className='inline-block whitespace-nowrap'>
                                {Array.from(part).map((char, index) => (
                                    <span
                                        key={`${char}-${index}`}
                                        data-char
                                        className={cn('inline-block whitespace-pre', charClassName)}
                                    >
                                        {char}
                                    </span>
                                ))}
                            </span>
                        )
                ))}
            </span>
        </span>
    )
}

export default SplitChars
